/**
 * PricingPage
 * Public pricing page with bundles, add-ons, and cart functionality
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  LocalOffer as PricingIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import {
  PricingTable,
  AddOnSelector,
  CartDrawer,
  CartBadge,
  UpsellModal,
} from '../components/commerce';
import { trackPageView } from '../lib/analytics';

const PricingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [cartOpen, setCartOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [lastAddedBundle, setLastAddedBundle] = useState<string | undefined>();

  // Track page view on mount
  React.useEffect(() => {
    trackPageView('pricing');
  }, []);

  const handleBundleAdd = (bundleId: string) => {
    setLastAddedBundle(bundleId);
    setUpsellOpen(true);
  };

  const handleUpsellClose = () => {
    setUpsellOpen(false);
    setCartOpen(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            REDEX
          </Typography>

          <Button
            component={Link}
            to="/contact"
            startIcon={<PhoneIcon />}
            sx={{ mr: 2 }}
          >
            Talk to Engineering
          </Button>

          <CartBadge onClick={() => setCartOpen(true)} />
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <PricingIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h3" fontWeight="bold">
                Pricing
              </Typography>
            </Box>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
              Choose the package that fits your business needs
            </Typography>
            <Typography variant="body1" color="text.secondary">
              All packages include hosting, backups, and security monitoring
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Table */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <PricingTable onBundleAdd={handleBundleAdd} />
      </Container>

      {/* Add-Ons Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.5), py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" textAlign="center" fontWeight="bold" gutterBottom>
            Customize Your Package
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Add individual services to any bundle
          </Typography>

          <AddOnSelector excludeSkuIds={['core_build']} />
        </Container>
      </Box>

      {/* Value Props Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                Deliverability Pack
              </Typography>
              <Typography variant="body1" paragraph>
                SPF/DKIM/DMARC + SES/SendGrid integration + warmup. Stops lead loss to spam. 
                Implemented in 2 business days.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Most businesses lose 15-30% of their emails to spam filters. Our deliverability
                pack ensures your messages reach inboxes, not junk folders.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                Ops Enablement (SOP)
              </Typography>
              <Typography variant="body1" paragraph>
                Signed SOP + dashboards + drill. Ensures daily lead triage (DialPad-first).
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Standard operating procedures reduce training time by 60% and ensure consistent
                lead handling across your entire team.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                Audit & Evidence Vault
              </Typography>
              <Typography variant="body1" paragraph>
                Cutover report, logs, rollback plan—de-risks procurement and M&A diligence.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete documentation package for compliance audits, due diligence, and
                enterprise procurement requirements.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                Maintenance Plus
              </Typography>
              <Typography variant="body1" paragraph>
                Patches, backups, uptime checks, rolling updates (up to 10h/month).
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Peace of mind with proactive maintenance, security updates, and dedicated
                engineering hours each month.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            Choose a package above or talk to our engineering team for custom solutions
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setCartOpen(true)}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              mr: 2,
              '&:hover': {
                bgcolor: alpha('#fff', 0.9),
              },
            }}
          >
            View Cart
          </Button>
          <Button
            variant="outlined"
            size="large"
            component={Link}
            to="/contact"
            sx={{
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                borderColor: 'white',
                bgcolor: alpha('#fff', 0.1),
              },
            }}
          >
            Talk to Engineering
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'background.paper', py: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" textAlign="center">
            © {new Date().getFullYear()} REDEX. All rights reserved. |{' '}
            <Link to="/terms.html" style={{ color: 'inherit' }}>Terms</Link> |{' '}
            <Link to="/privacy.html" style={{ color: 'inherit' }}>Privacy</Link>
          </Typography>
        </Container>
      </Box>

      {/* Cart Drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Upsell Modal */}
      <UpsellModal
        open={upsellOpen}
        onClose={handleUpsellClose}
        triggeredBy={lastAddedBundle}
      />
    </Box>
  );
};

export default PricingPage;
